import { useState, useEffect } from 'react'
import { calculateOdometer, detectTrips, getAverageEfficiency } from '../utils/odometer'
import { timeAgo } from '../utils/events'
import { isUnconfigured } from '../utils/device'
import { useSettings } from '../api/settings'
import { useIsMobile } from '../hooks/useIsMobile'
import { getTruckBattery } from '../api/tankguard'
import { getUser } from '../api/auth'
import FuelChart from './FuelChart'
import { ChartSkeleton, TruckRowSkeleton } from './Skeleton'
import { IconAlertTriangle, IconDroplet } from '@tabler/icons-react'
import Drawer from './ui/Drawer'

// 7-point ext-voltage sparkline + trend badge. battery is the payload returned
// by GET /api/trucks/:id/battery; renders nothing while it's still loading or
// when we don't have at least two daily samples to draw a line through.
function BatterySparkline({ battery }) {
  if (!battery) return null;
  const samples = (battery.samples || []).filter(s => s.ext_mv != null && s.ext_mv > 0).slice(-7);
  if (samples.length < 2) {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tg-text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Battery health</div>
        <div style={{ fontSize: 12, color: 'var(--tg-text-muted)', padding: '8px 0' }}>Need a few more daily readings before a trend appears.</div>
      </div>
    );
  }
  const W = 200, H = 48, padX = 4, padY = 6;
  const vals = samples.map(s => s.ext_mv);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = Math.max(50, max - min); // never collapse to a flat line
  const X = i => padX + (i / (samples.length - 1)) * (W - 2 * padX);
  const Y = v => padY + (1 - (v - min) / span) * (H - 2 * padY);
  const d = samples.map((s, i) => (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(s.ext_mv).toFixed(1)).join(' ');
  const trend = battery.trend;
  const declining = !!(trend && trend.declining);
  const stroke = declining ? 'var(--tg-danger-500)' : 'var(--tg-teal-500)';
  const latest = vals[vals.length - 1];
  const alert = declining && trend.days_to_failure != null && trend.days_to_failure < 14;
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tg-text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Battery health</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 10px', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-md, 6px)' }}>
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} style={{ flexShrink: 0 }}>
          <line x1={padX} y1={Y(10000)} x2={W - padX} y2={Y(10000)} stroke="var(--tg-danger-300, #fca5a5)" strokeDasharray="3 3" strokeWidth="0.75" />
          <path d={d} fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          {samples.map((s, i) => (
            <circle key={i} cx={X(i)} cy={Y(s.ext_mv)} r={i === samples.length - 1 ? 2.5 : 1.5} fill={stroke} />
          ))}
        </svg>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tg-text-primary)' }}>
            {(latest / 1000).toFixed(2)} V
            <span style={{ fontSize: 10, marginLeft: 8, color: declining ? 'var(--tg-danger-700)' : 'var(--tg-text-muted)', fontWeight: 500 }}>
              {trend ? (trend.slope_mv_per_day > 0 ? '+' : '') + trend.slope_mv_per_day + ' mV/day' : 'building trend…'}
            </span>
          </div>
          <div style={{ fontSize: 10, color: alert ? 'var(--tg-danger-700)' : 'var(--tg-text-muted)', marginTop: 2, fontWeight: alert ? 600 : 400 }}>
            {alert ? `⚠ Predicted failure in ${trend.days_to_failure} day${trend.days_to_failure === 1 ? '' : 's'}` :
             declining ? `Declining · ${trend.days_to_failure} day${trend.days_to_failure === 1 ? '' : 's'} to 10 V` :
             'Voltage stable'}
          </div>
        </div>
      </div>
    </div>
  );
}

function fmt(ts) {
  return new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

const LEDGER_TYPE_STYLE = {
  consumed_driving: { label: 'Driving',   color: 'var(--tg-text-primary)',  bg: 'transparent' },
  consumed_idle:    { label: 'Idle',      color: '#92400e',                 bg: 'var(--tg-warning-50, #fef3c7)' },
  refueled:         { label: 'Refuel',    color: 'var(--tg-teal-700)',      bg: 'var(--tg-teal-50)' },
  theft:            { label: 'Theft',     color: 'var(--tg-danger-700)',    bg: 'var(--tg-danger-50)' },
  correction:       { label: 'Adjust',    color: 'var(--tg-text-secondary)', bg: 'var(--tg-bg-subtle)' },
};

// The fuel ledger is the per-truck "bank statement" — every meaningful diesel
// movement in the last 24h with a price tag attached. The summary line up top
// is what the morning ops review actually reads; the table is the receipt.
function FuelLedgerSection({ ledger, loading }) {
  if (loading) {
    return (
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tg-text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fuel ledger — last 24h</div>
        <div style={{ fontSize: 12, color: 'var(--tg-text-muted)', padding: '8px 0' }}>Loading…</div>
      </div>
    );
  }
  if (!ledger) return null;
  const s = ledger.summary || {};
  const entries = ledger.entries || [];
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tg-text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fuel ledger — last 24h</div>

      <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginBottom: 10, lineHeight: 1.5 }}>
        Burned <strong style={{ color: 'var(--tg-text-primary)' }}>{(s.total_consumed_l || 0).toFixed(1)}L</strong>
        <span style={{ color: 'var(--tg-text-muted)' }}> (€{Math.round(s.total_consumed_eur || 0)})</span>
        {' · Refueled '}<strong style={{ color: 'var(--tg-teal-700)' }}>{(s.total_refueled_l || 0).toFixed(0)}L</strong>
        {' · Idle '}<strong style={{ color: '#92400e' }}>{(s.idle_l || 0).toFixed(1)}L</strong>
        {(s.total_theft_l || 0) > 0 && (
          <>
            {' · '}
            <strong style={{ color: 'var(--tg-danger-700)' }}>Theft {(s.total_theft_l).toFixed(1)}L (€{Math.round(s.total_theft_eur || 0)})</strong>
          </>
        )}
      </div>

      {entries.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--tg-text-muted)', padding: '8px 0' }}>No ledger entries yet — aggregator hasn't run, or the truck hasn't moved.</div>
      ) : (
        <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-md, 6px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: 'var(--tg-bg-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--tg-text-tertiary)', fontSize: 10 }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Time</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Type</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>Liters</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>Cost</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 500 }}>L/100km</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => {
                const style = LEDGER_TYPE_STYLE[e.entry_type] || LEDGER_TYPE_STYLE.correction;
                return (
                  <tr key={e.id} style={{ borderTop: '1px solid var(--tg-border-default)' }}>
                    <td style={{ padding: '6px 8px', color: 'var(--tg-text-secondary)' }}>{fmt(e.occurred_at)}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <span style={{ background: style.bg, color: style.color, padding: '1px 6px', borderRadius: 4, fontWeight: 500, fontSize: 10 }}>{style.label}</span>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: style.color, fontWeight: 500 }}>
                      {e.entry_type === 'refueled' ? '+' : e.entry_type === 'theft' ? '−' : ''}{Number(e.liters).toFixed(1)}L
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--tg-text-secondary)' }}>
                      {e.cost_eur != null ? '€' + Number(e.cost_eur).toFixed(2) : '—'}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--tg-text-muted)' }}>
                      {e.efficiency_l_per_100km != null ? Number(e.efficiency_l_per_100km).toFixed(1) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TruckDetail({ truck, history, ledger, events, onClose, historyLoading }) {
  const settings = useSettings();
  const DIESEL = settings.diesel;
  const mobile = useIsMobile();
  const [battery, setBattery] = useState(null);

  const open = !!truck

  useEffect(() => {
    if (!truck) { setBattery(null); return; }
    let cancelled = false;
    getTruckBattery(truck.id)
      .then(d => { if (!cancelled) setBattery(d); })
      .catch(() => { if (!cancelled) setBattery(null); });
    return () => { cancelled = true; };
  }, [truck?.id]);

  const odometer = open ? calculateOdometer(history) : 0;
  const trips = open ? detectTrips(history) : [];
  const efficiency = open ? getAverageEfficiency(trips) : null;
  const truckEvents = open ? (events || []).filter(e => e.truck === truck.name) : [];
  const fuelEur = open ? Math.round((truck.fuel_liters || 0) * DIESEL) : 0;
  const isDual = open && truck.tanks === 2;
  const f1 = (truck?.fuel1_liters ?? truck?.fuel_liters ?? 0);
  const f2 = (truck?.fuel2_liters ?? 0);

  const fuelTiles = isDual
    ? [
        { label: 'Fuel left', value: truck.online ? f1.toFixed(1) + ' L' : '—', sub: truck.tank1_max_liters ? 'of ' + truck.tank1_max_liters + ' L cap' : '—' },
        { label: 'Fuel right', value: truck.online ? f2.toFixed(1) + ' L' : '—', sub: truck.tank2_max_liters ? 'of ' + truck.tank2_max_liters + ' L cap' : '—' },
      ]
    : [
        { label: 'Fuel level', value: truck && truck.online ? (truck.fuel_liters || 0).toFixed(1) + ' L' : '—', sub: truck && truck.online ? '€' + fuelEur + ' value' : 'No signal' },
      ];

  // Hardware diagnostic tiles only render for admin/superadmin AND only when
  // the API surfaced the fields (regular users get them stripped server-side).
  const isAdmin = open && ['admin', 'superadmin'].includes(getUser()?.role);
  const sBat = open ? truck.sensor_battery_pct : null;
  const sBatColor = sBat == null
    ? 'var(--tg-text-primary)'
    : sBat < 20 ? 'var(--tg-danger-700)'
    : sBat < 50 ? '#92400e'
    : 'var(--tg-success-700, #047857)';

  const otherTiles = open ? [
    { label: 'Odometer (24h)', value: odometer > 0 ? odometer + ' km' : '—', sub: trips.length + ' trip' + (trips.length !== 1 ? 's' : '') + ' today' },
    { label: 'Avg efficiency', value: efficiency ? efficiency + ' L/100km' : '—', sub: efficiency ? (efficiency < 20 ? 'Good' : efficiency < 30 ? 'Average' : 'High') : 'Need trip data' },
    { label: 'Sensor quality', value: truck.fuel_quality > 0 ? '★'.repeat(truck.fuel_quality || 0) : '—', sub: truck.voltage ? (truck.voltage / 1000).toFixed(2) + 'V ext power' : 'No data' },
    // Layer-1 beacon fields (Phase 2). Full 0-255 acoustic confidence + movement
    // signals — the raw inputs behind theft-verdict fusion. Shown to all roles.
    ...(truck.acoustic_confidence != null ? [{
      label: 'Sensor confidence',
      value: truck.acoustic_confidence + '/255',
      sub: truck.acoustic_confidence >= 192 ? 'High — readings trusted'
         : truck.acoustic_confidence >= 96 ? 'Medium confidence'
         : 'Low — readings noisy',
      valueColor: truck.acoustic_confidence >= 192 ? 'var(--tg-success-700, #047857)'
                : truck.acoustic_confidence >= 96 ? '#92400e'
                : 'var(--tg-danger-700)',
    }] : []),
    ...((truck.tilt_magnitude != null || truck.vibration_intensity != null) ? [{
      label: 'Movement',
      value: 'Tilt ' + (truck.tilt_magnitude ?? '—') + ' · Vib ' + (truck.vibration_intensity ?? '—'),
      sub: 'Tilt / vibration (0–255)',
    }] : []),
    ...(isAdmin && truck.power_connected !== undefined ? [{
      label: 'Power supply',
      value: truck.power_connected ? 'Connected' : 'On battery',
      // Green = truck is feeding the tracker. Amber = running on the internal
      // LiPo, which means engine off / blown fuse / unplugged — worth flagging.
      valueColor: truck.power_connected ? 'var(--tg-success-700, #047857)' : '#92400e',
      sub: truck.power_voltage_v
        ? truck.power_voltage_v + ' ext power'
        : truck.tracker_battery_pct != null
          ? 'Backup battery ' + truck.tracker_battery_pct + '%'
          : 'No power data',
    }] : []),
    ...(isAdmin && sBat != null ? [{
      label: 'Sensor battery',
      value: sBat + '%',
      sub: sBat < 10 ? '⚠ Critical — replace now'
         : sBat < 20 ? '⚠ Replace within 30 days'
         : sBat < 50 ? 'Getting low'
         : 'Good',
      valueColor: sBatColor,
    }] : []),
  ] : [];

  return (
    <Drawer open={open} onClose={onClose} title={truck?.name || ''} width={mobile ? undefined : 420}>
      {truck && (
        <>
          {/* Status sub-heading */}
          <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: (isUnconfigured(truck) || !truck.online) ? 'var(--tg-graphite-300)' : 'var(--tg-status-online)', flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
              {isUnconfigured(truck)
                ? (truck.connectivity === 'never_connected' ? 'Never connected' : 'No device assigned')
                : truck.online
                  ? truck.speed > 2
                    ? 'Moving · ' + Math.round(truck.speed) + ' km/h'
                    : (truck.current_address || 'Stationary')
                  : 'Offline'}
            </span>
            {isDual && (
              <span style={{ background: 'var(--tg-teal-50)', color: 'var(--tg-teal-700)', fontSize: 10, padding: '1px 6px', borderRadius: 4, marginLeft: 4, fontWeight: 600 }}>Dual tank</span>
            )}
            {truck.hasAlert && (
              <span style={{ background: 'var(--tg-danger-50)', color: 'var(--tg-danger-700)', fontSize: 10, padding: '1px 6px', borderRadius: 4, marginLeft: 4, fontWeight: 600 }}>Alert</span>
            )}
            {truck.efficiency_score != null && (
              <span
                title="Efficiency score (0-100): combines today's consumption vs baseline, theft events, and any active anomaly."
                style={{
                  background: truck.efficiency_score >= 80 ? 'var(--tg-success-50, #ecfdf5)'
                            : truck.efficiency_score >= 60 ? 'var(--tg-warning-50, #fef3c7)'
                            : 'var(--tg-danger-50)',
                  color: truck.efficiency_score >= 80 ? 'var(--tg-success-700, #047857)'
                       : truck.efficiency_score >= 60 ? '#92400e'
                       : 'var(--tg-danger-700)',
                  fontSize: 10, padding: '1px 6px', borderRadius: 4, marginLeft: 'auto', fontWeight: 600,
                }}
              >
                Score {truck.efficiency_score}
              </span>
            )}
            {truck.has_consumption_anomaly && (
              <span style={{ background: truck.anomaly_severity === 'high' ? 'var(--tg-danger-50)' : 'var(--tg-warning-50, #fef3c7)', color: truck.anomaly_severity === 'high' ? 'var(--tg-danger-700)' : '#92400e', fontSize: 10, padding: '1px 6px', borderRadius: 4, marginLeft: 4, fontWeight: 600 }}>
                ⚠ {truck.anomaly_severity}
              </span>
            )}
          </div>

          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1px', background: 'var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)', overflow: 'hidden', marginBottom: 16 }}>
            {[...fuelTiles, ...otherTiles].map(s => (
              <div key={s.label} style={{ background: 'var(--tg-bg-surface)', padding: '12px 14px' }}>
                <div style={{ fontSize: 10, color: 'var(--tg-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>{s.label}</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: s.valueColor || 'var(--tg-text-primary)', letterSpacing: '-0.01em' }}>{s.value}</div>
                <div style={{ fontSize: 10, color: 'var(--tg-text-muted)', marginTop: 2 }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {isDual && truck.online && (
            <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginBottom: 16 }}>
              Combined: <strong style={{ color: 'var(--tg-text-primary)' }}>{(f1 + f2).toFixed(1)} L</strong>
              <span style={{ color: 'var(--tg-text-muted)' }}> · €{Math.round((f1 + f2) * DIESEL)} value</span>
            </div>
          )}

          {/* Fuel chart */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tg-text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fuel history — last 24h</div>
            {historyLoading ? <ChartSkeleton height={160} /> : <FuelChart history={history} events={truckEvents} />}
          </div>

          {/* Fuel ledger — diesel "bank statement" for the last 24h */}
          <FuelLedgerSection ledger={ledger} loading={historyLoading} />

          {/* Battery health sparkline — 7-day ext-voltage trend with failure ETA */}
          <BatterySparkline battery={battery} />

          {/* Trips */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tg-text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Trips today</div>
            {historyLoading && (
              <>
                <TruckRowSkeleton />
                <TruckRowSkeleton />
              </>
            )}
            {!historyLoading && trips.length === 0 && <div style={{ fontSize: 12, color: 'var(--tg-text-muted)', padding: '8px 0' }}>No trips recorded in last 24h</div>}
            {!historyLoading && trips.map((trip, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < trips.length - 1 ? '0.5px solid var(--tg-border-default)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--tg-text-primary)' }}>{fmt(trip.start)} — {fmt(trip.end)}</div>
                  <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginTop: 2 }}>
                    {trip.duration} min · {trip.distance} km{trip.fuelUsed ? ' · ' + trip.fuelUsed.toFixed(1) + 'L used' : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: trip.efficiency ? (trip.efficiency > 30 ? 'var(--tg-status-danger)' : trip.efficiency > 20 ? 'var(--tg-status-warning)' : 'var(--tg-status-online)') : 'var(--tg-text-muted)' }}>
                    {trip.efficiency ? trip.efficiency + ' L/100' : '—'}
                  </div>
                  {trip.fuelUsed && <div style={{ fontSize: 10, color: 'var(--tg-text-muted)' }}>€{Math.round(trip.fuelUsed * DIESEL)}</div>}
                </div>
              </div>
            ))}
          </div>

          {/* Alerts */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tg-text-primary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alerts (24h)</div>
            {truckEvents.length === 0 && <div style={{ fontSize: 12, color: 'var(--tg-text-muted)', padding: '8px 0' }}>No alerts in last 24h</div>}
            {truckEvents.map((ev, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < truckEvents.length - 1 ? '0.5px solid var(--tg-border-default)' : 'none', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 26, height: 26, borderRadius: 6, background: ev.type === 'theft' ? 'var(--tg-danger-50)' : 'var(--tg-success-50)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {ev.type === 'theft'
                    ? <IconAlertTriangle size={13} color="var(--tg-danger-700)" />
                    : <IconDroplet size={13} color="var(--tg-success-700)" />
                  }
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: ev.type === 'theft' ? 'var(--tg-danger-700)' : 'var(--tg-success-700)' }}>
                    {ev.type === 'theft' ? 'Theft detected' : 'Refuel'}{ev.side ? ' · ' + (ev.side === 'L' ? 'Left tank' : 'Right tank') : ''}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--tg-text-secondary)', marginTop: 1 }}>
                    {ev.type === 'theft' ? '-' + ev.liters.toFixed(0) + 'L · €' + ev.eur + ' loss' : '+' + ev.liters.toFixed(0) + 'L · €' + ev.eur}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: 'var(--tg-text-muted)', whiteSpace: 'nowrap' }}>{timeAgo(ev.ts)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
}
